/**
 * @module hooks/useChartsQuickDragList
 * @description 从 chartData 提取手动排序拖拽列表（对齐 ChartsDrop/hooks chartsQuickDragList）
 */
import { useMemo } from 'react';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { getNullTextOfFilter } from '@/pages/datalab/utils/utils';
import { useQuerySelector, useResultSelector } from './deps';
import { toChartTypeEnum } from '../utils/toChartTypeEnum';

export interface IChartsQuickDragItem {
  id: string | number;
  value: string;
}

const MAX_DRAG_ITEMS = 1000;

/** 用于 useMemo 依赖：chartData 引用不变时仍能感知轴类目变化 */
function pickAxisSourceForQuickDrag(
  chartType: TChartType,
  chartData: Record<string, unknown> | undefined,
): unknown {
  if (!chartData) return null;

  if (chartType === ChartType.MixedChart) {
    return (chartData.mixedChartData as Record<string, unknown> | undefined)
      ?.x_axis_data;
  }

  if (
    [
      ChartType.Line,
      ChartType.EventLine,
      ChartType.Bar,
      ChartType.StackBar,
    ].includes(chartType)
  ) {
    const data =
      (chartData.lineChart as Record<string, unknown> | undefined)
      || (chartData.eventLineChart as Record<string, unknown> | undefined)
      || (chartData.barChart as Record<string, unknown> | undefined)
      || (chartData.stackBarChart as Record<string, unknown> | undefined);
    return data?.xAxisData;
  }

  if (chartType === ChartType.HorizontalBarChart) {
    return (
      chartData.horizontalBarData as Array<Record<string, unknown>> | undefined
    )?.[0]?.yAxis;
  }

  if (chartType === ChartType.PercentStackBar) {
    return (
      chartData.percentStackBarData as Record<string, unknown> | undefined
    )?.xAxisData;
  }

  if (chartType === ChartType.PercentLine) {
    return (
      chartData.percentLineChartData as Record<string, unknown> | undefined
    )?.xAxisData;
  }

  return chartData.xAxis;
}

function mapAxisValues(
  values: Array<string | number | null> | undefined,
): IChartsQuickDragItem[] {
  if (!Array.isArray(values)) return [];
  return values.slice(0, MAX_DRAG_ITEMS).map((raw) => {
    const text = getNullTextOfFilter(raw);
    return {
      value: text,
      id: text || 'custom_undefined',
    };
  });
}

/**
 * 按图表类型从 ResultContext chartData 提取 X 轴（或等价轴）类目列表
 */
export function extractChartsQuickDragList(
  chartType: TChartType,
  chartData: Record<string, unknown> | undefined,
): IChartsQuickDragItem[] {
  if (!chartData) return [];

  try {
    if (chartType === ChartType.MixedChart) {
      const mixedXAxis = (chartData.mixedChartData as Record<string, unknown>)
        ?.x_axis_data as Array<string | number | null> | undefined;
      return mapAxisValues(mixedXAxis);
    }

    if (
      [
        ChartType.Line,
        ChartType.EventLine,
        ChartType.Bar,
        ChartType.StackBar,
      ].includes(chartType)
    ) {
      const data =
        (chartData.lineChart as Record<string, unknown> | undefined)
        || (chartData.eventLineChart as Record<string, unknown> | undefined)
        || (chartData.barChart as Record<string, unknown> | undefined)
        || (chartData.stackBarChart as Record<string, unknown> | undefined);
      return mapAxisValues(
        data?.xAxisData as Array<string | number | null> | undefined,
      );
    }

    if (chartType === ChartType.HorizontalBarChart) {
      const yAxis = (
        chartData.horizontalBarData as Array<Record<string, unknown>> | undefined
      )?.[0]?.yAxis as Array<string | number | null> | undefined;
      return mapAxisValues(yAxis);
    }

    if (chartType === ChartType.PercentStackBar) {
      const xAxisData = (
        chartData.percentStackBarData as Record<string, unknown> | undefined
      )?.xAxisData as Array<string | number | null> | undefined;
      return mapAxisValues(xAxisData);
    }

    if (chartType === ChartType.PercentLine) {
      const xAxisData = (
        chartData.percentLineChartData as Record<string, unknown> | undefined
      )?.xAxisData as Array<string | number | null> | undefined;
      return mapAxisValues(xAxisData);
    }

    return mapAxisValues(chartData.xAxis as Array<string | number | null> | undefined);
  } catch (error) {
    console.log('[ChartSettingsV2] extractChartsQuickDragList failed', {
      chartType,
      error,
    });
    return [];
  }
}

export function useChartsQuickDragList(): IChartsQuickDragItem[] {
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const chartData = useResultSelector(s => s.chartData);
  const chartDataRecord = chartData as Record<string, unknown> | undefined;
  const axisSource = pickAxisSourceForQuickDrag(chartType, chartDataRecord);

  return useMemo(
    () => extractChartsQuickDragList(chartType, chartDataRecord),
    [chartType, chartDataRecord, axisSource],
  );
}

